/* =====================================================================
 * User Store — 유저 정보, 플랜, 역할, 사용량 전역 관리 (Zustand)
 * ===================================================================== */

import { create } from 'zustand';
import i18n from '@auw/i18n';
import {
  getAuthMe,
  AuthMeError,
  getUserUsage,
  updateMyProfile,
  deleteMyAccount,
  updateConsents as apiUpdateConsents,
  // updateUserPlan as apiUpdatePlan, // TODO: 결제 연동 후 활성화
  type AuthMeResponse,
  type ProfileUpdateRequest,
  type ConsentsMap,
  type ConsentUpdateItem,
} from '@auw/api-client';
import type { PlanType, UserRole } from '@auw/workspace-utils';
import type { WorkspaceMode } from '@auw/workspace-utils';
import { PLANS, gbToBytes } from '@auw/workspace-utils';
import { activeTenantKey as tenantKey } from '@auw/tenant';

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export type TenantIdentityValue = string | number | boolean | null;
export type TenantIdentityMetadata = Record<string, TenantIdentityValue>;

export interface UserInfo {
  userId: string;
  email: string | null;
  name: string | null;
  picture: string | null;
  hasCustomPicture: boolean;
  company: string | null;
  department: string | null;
  tenantIdentity: TenantIdentityMetadata | null;
  preferredLanguage: string | null;
  countryCode: string | null;
  timezone: string | null;
  sidebarFavoriteModes: WorkspaceMode[];
  toolsFavoriteIds: string[];
  jobRole: string | null;
  interests: string[];
  customInterests: string[];
  feedDeliveryHour: number;
  feedDeliveryHourLockedUntil: string | null;
  onboardingCompleted: boolean;
  role: UserRole;
  plan: PlanType;
  consents: ConsentsMap | null;
}

export interface UsageInfo {
  tokensUsed: number;
  tokensLimit: number;
  storageUsedBytes: number;
  storageLimitBytes: number;
}

interface UserState {
  /* data */
  user: UserInfo | null;
  usage: UsageInfo;
  isLoaded: boolean;

  /* actions */
  loadUser: () => Promise<void>;
  refreshUsage: () => Promise<void>;
  applyUsageSnapshot: (next: Partial<UsageInfo>) => void;
  updatePlan: (plan: PlanType) => Promise<boolean>;
  updateProfile: (fields: ProfileUpdateRequest) => Promise<void>;
  updateConsents: (items: ConsentUpdateItem[]) => Promise<ConsentsMap>;
  updateSidebarFavoriteModes: (modes: WorkspaceMode[]) => Promise<void>;
  updateToolsFavoriteIds: (ids: string[]) => Promise<void>;
  deleteAccount: () => Promise<void>;
  clear: () => void;
}

/* ------------------------------------------------------------------ */
/* Defaults                                                            */
/* ------------------------------------------------------------------ */

const DEFAULT_USAGE: UsageInfo = {
  tokensUsed: 0,
  tokensLimit: PLANS.free.tokens,
  storageUsedBytes: 0,
  storageLimitBytes: gbToBytes(PLANS.free.storageGB),
};

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/** 백엔드 응답 기반 역할 결정 */
function resolveRole(backendRole?: string | null, email?: string | null): UserRole {
  // 백엔드가 명시적으로 보내준 역할 우선
  if (backendRole === 'owner' || backendRole === 'admin' || backendRole === 'user') {
    return backendRole;
  }
  void email;
  return 'user';
}

/** 플랜 결정 (백엔드 응답 우선) */
function resolvePlan(backendPlan?: string | null): PlanType {
  if (backendPlan && backendPlan in PLANS) return backendPlan as PlanType;
  return 'free';
}

function usageLimitsForPlan(plan: PlanType): { tokensLimit: number; storageLimitBytes: number } {
  const p = PLANS[plan];
  return {
    tokensLimit: p.tokens,
    storageLimitBytes: gbToBytes(p.storageGB),
  };
}

const DEFAULT_FEED_DELIVERY_HOUR = 9;
const VALID_SIDEBAR_FAVORITE_MODES = new Set<WorkspaceMode>([
  'agent',
  'document',
  'storage',
  'message',
  'app-manage',
  'admin-page',
  'plugin',
  'widget',
  'feed',
  'tools',
]);

function normalizeFeedDeliveryHour(value: unknown): number {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 23) {
    return value;
  }
  return DEFAULT_FEED_DELIVERY_HOUR;
}

function normalizeNullableString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function normalizeTenantIdentity(value: unknown): TenantIdentityMetadata | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const out: TenantIdentityMetadata = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    if (!key.trim()) continue;
    if (
      typeof raw === 'string' ||
      typeof raw === 'number' ||
      typeof raw === 'boolean' ||
      raw === null
    ) {
      out[key] = raw;
    }
  }
  return Object.keys(out).length > 0 ? out : null;
}

function normalizeSidebarFavoriteModes(value: unknown): WorkspaceMode[] {
  if (!Array.isArray(value)) return [];
  const out: WorkspaceMode[] = [];
  const seen = new Set<WorkspaceMode>();

  for (const item of value) {
    if (typeof item !== 'string') continue;
    const normalized = item.trim() as WorkspaceMode;
    if (!VALID_SIDEBAR_FAVORITE_MODES.has(normalized) || seen.has(normalized)) continue;
    seen.add(normalized);
    out.push(normalized);
  }

  return out;
}

function normalizeToolsFavoriteIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    if (typeof item !== 'string') continue;
    const id = item.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

function normalizeCustomInterests(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  const seen = new Set<string>();

  for (const item of value) {
    if (typeof item !== 'string') continue;
    const label = item.replace(/\s+/g, ' ').trim();
    const normalized = label.toLowerCase();
    if (!label || seen.has(normalized)) continue;
    seen.add(normalized);
    out.push(label);
  }

  return out;
}

function mapAuthToUser(me: AuthMeResponse): UserInfo {
  const email = me.email ?? null;
  const role = resolveRole(me.role, email);
  const plan = resolvePlan(me.plan);

  return {
    userId: me.user_id,
    email,
    name: me.name ?? null,
    picture: me.picture ?? null,
    hasCustomPicture: Boolean(me.has_custom_picture),
    company: me.company ?? null,
    department: me.department ?? null,
    tenantIdentity: normalizeTenantIdentity(me.tenant_identity),
    preferredLanguage: me.preferred_language ?? null,
    countryCode: me.country_code ?? null,
    timezone: me.timezone ?? null,
    sidebarFavoriteModes: normalizeSidebarFavoriteModes(me.sidebar_favorite_modes),
    toolsFavoriteIds: normalizeToolsFavoriteIds(me.tools_favorite_ids),
    jobRole: me.job_role ?? null,
    interests: Array.isArray(me.interests) ? me.interests : [],
    customInterests: normalizeCustomInterests(me.custom_interests),
    feedDeliveryHour: normalizeFeedDeliveryHour(me.feed_delivery_hour),
    feedDeliveryHourLockedUntil: normalizeNullableString(me.feed_delivery_hour_locked_until),
    onboardingCompleted: typeof me.onboarding_completed === 'boolean' ? me.onboarding_completed : true,
    role,
    plan,
    consents: me.consents ?? null,
  };
}

function mapAuthToUsage(me: AuthMeResponse, plan: PlanType): UsageInfo {
  const limits = usageLimitsForPlan(plan);
  return {
    tokensUsed: me.tokens_used ?? 0,
    tokensLimit: me.tokens_limit ?? limits.tokensLimit,
    storageUsedBytes: me.storage_used_bytes ?? 0,
    storageLimitBytes: me.storage_limit_bytes ?? limits.storageLimitBytes,
  };
}

/* ------------------------------------------------------------------ */
/* Session cache — accountId-scoped, sessionStorage (tab-safe)         */
/* ------------------------------------------------------------------ */

const USER_CACHE_KEY = tenantKey('user_cache');
let sidebarFavoriteModesRequestSeq = 0;
let toolsFavoriteIdsRequestSeq = 0;

interface CachedUserSnapshot {
  accountId: string;
  user: UserInfo;
  usage: UsageInfo;
}

function normalizeCachedUser(user: UserInfo): UserInfo {
  const legacyUser = user as Partial<UserInfo>;
  return {
    ...user,
    hasCustomPicture: Boolean(legacyUser.hasCustomPicture),
    tenantIdentity: normalizeTenantIdentity(legacyUser.tenantIdentity),
    preferredLanguage: legacyUser.preferredLanguage ?? null,
    countryCode: legacyUser.countryCode ?? null,
    timezone: legacyUser.timezone ?? null,
    sidebarFavoriteModes: normalizeSidebarFavoriteModes(legacyUser.sidebarFavoriteModes),
    toolsFavoriteIds: normalizeToolsFavoriteIds(legacyUser.toolsFavoriteIds),
    jobRole: legacyUser.jobRole ?? null,
    interests: Array.isArray(legacyUser.interests) ? legacyUser.interests : [],
    customInterests: normalizeCustomInterests(legacyUser.customInterests),
    feedDeliveryHour: normalizeFeedDeliveryHour(legacyUser.feedDeliveryHour),
    feedDeliveryHourLockedUntil: normalizeNullableString(legacyUser.feedDeliveryHourLockedUntil),
    onboardingCompleted: typeof legacyUser.onboardingCompleted === 'boolean' ? legacyUser.onboardingCompleted : true,
    consents: legacyUser.consents ?? null,
  };
}

function saveUserCache(accountId: string, user: UserInfo, usage: UsageInfo): void {
  try {
    sessionStorage.setItem(
      USER_CACHE_KEY,
      JSON.stringify({ accountId, user, usage } satisfies CachedUserSnapshot),
    );
  } catch { /* quota exceeded — ignore */ }
}

export function restoreUserCache(expectedAccountId: string): CachedUserSnapshot | null {
  try {
    const raw = sessionStorage.getItem(USER_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedUserSnapshot;
    if (parsed.accountId !== expectedAccountId) return null;
    if (!parsed.user?.userId) return null;
    return {
      ...parsed,
      user: normalizeCachedUser(parsed.user),
    };
  } catch {
    return null;
  }
}

function clearUserCache(): void {
  try { sessionStorage.removeItem(USER_CACHE_KEY); } catch { /* ignore */ }
}

/* ------------------------------------------------------------------ */
/* Store                                                               */
/* ------------------------------------------------------------------ */

export const useUserStore = create<UserState>((set, get) => ({
  user: null,
  usage: DEFAULT_USAGE,
  isLoaded: false,

  loadUser: async () => {
    try {
      const me = await getAuthMe();
      const user = mapAuthToUser(me);
      const usage = mapAuthToUsage(me, user.plan);
      set({ user, usage, isLoaded: true });

      const accountId = me.user_id;
      if (accountId) saveUserCache(accountId, user, usage);
    } catch (err) {
      const isTransient =
        err instanceof AuthMeError && (err.status === 429 || err.status >= 500);

      if (isTransient && get().user) {
        set({ isLoaded: true });
        return;
      }

      set({ user: null, usage: DEFAULT_USAGE, isLoaded: true });
    }
  },

  refreshUsage: async () => {
    try {
      const data = await getUserUsage();
      set({
        usage: {
          tokensUsed: data.tokens_used,
          tokensLimit: data.tokens_limit,
          storageUsedBytes: data.storage_used_bytes,
          storageLimitBytes: data.storage_limit_bytes,
        },
      });
    } catch {
      // 실패 시 기존 값 유지
    }
  },

  applyUsageSnapshot: (next) => {
    set((state) => ({
      usage: {
        ...state.usage,
        ...(typeof next.tokensUsed === 'number' ? { tokensUsed: Math.max(0, next.tokensUsed) } : {}),
        ...(typeof next.tokensLimit === 'number' ? { tokensLimit: Math.max(0, next.tokensLimit) } : {}),
        ...(typeof next.storageUsedBytes === 'number' ? { storageUsedBytes: Math.max(0, next.storageUsedBytes) } : {}),
        ...(typeof next.storageLimitBytes === 'number' ? { storageLimitBytes: Math.max(0, next.storageLimitBytes) } : {}),
      },
    }));
  },

  updatePlan: async (plan: PlanType) => {
    try {
      // TODO: 결제 연동 후 실제 API 호출로 교체
      // await apiUpdatePlan(plan);

      // Mock: 프론트엔드에서 즉시 플랜 변경
      const { user } = get();
      if (user) {
        const limits = usageLimitsForPlan(plan);

        set({
          user: { ...user, plan },
          usage: {
            ...get().usage,
            tokensLimit: limits.tokensLimit,
            storageLimitBytes: limits.storageLimitBytes,
          },
        });
      }
      return true;
    } catch {
      return false;
    }
  },

  updateProfile: async (fields) => {
    const { user } = get();
    if (!user) throw new Error(i18n.t('common:error.loginRequired'));

    const res = await updateMyProfile(fields);
    const nextUser: UserInfo = {
      ...user,
      name: fields.name !== undefined ? (res.name ?? null) : user.name,
      picture: fields.picture !== undefined ? (res.picture ?? null) : user.picture,
      hasCustomPicture:
        fields.picture !== undefined ? Boolean(res.has_custom_picture) : user.hasCustomPicture,
      company: fields.company !== undefined ? (res.company ?? null) : user.company,
      department: fields.department !== undefined ? (res.department ?? null) : user.department,
      preferredLanguage:
        fields.preferred_language !== undefined
          ? (res.preferred_language ?? null)
          : user.preferredLanguage,
      countryCode:
        fields.country_code !== undefined
          ? (res.country_code ?? null)
          : user.countryCode,
      timezone:
        fields.timezone !== undefined
          ? (res.timezone ?? null)
          : user.timezone,
      sidebarFavoriteModes:
        fields.sidebar_favorite_modes !== undefined
          ? normalizeSidebarFavoriteModes(res.sidebar_favorite_modes)
          : user.sidebarFavoriteModes,
      toolsFavoriteIds:
        fields.tools_favorite_ids !== undefined
          ? normalizeToolsFavoriteIds(res.tools_favorite_ids)
          : user.toolsFavoriteIds,
      jobRole:
        fields.job_role !== undefined
          ? (res.job_role ?? null)
          : user.jobRole,
      interests:
        fields.interests !== undefined
          ? (Array.isArray(res.interests) ? res.interests : [])
          : user.interests,
      customInterests:
        fields.custom_interests !== undefined
          ? normalizeCustomInterests(res.custom_interests)
          : user.customInterests,
      feedDeliveryHour:
        fields.feed_delivery_hour !== undefined
          ? normalizeFeedDeliveryHour(res.feed_delivery_hour)
          : user.feedDeliveryHour,
      feedDeliveryHourLockedUntil:
        fields.feed_delivery_hour !== undefined
          ? normalizeNullableString(res.feed_delivery_hour_locked_until)
          : user.feedDeliveryHourLockedUntil,
      onboardingCompleted:
        fields.onboarding_completed !== undefined
          ? (typeof res.onboarding_completed === 'boolean'
            ? res.onboarding_completed
            : user.onboardingCompleted)
          : user.onboardingCompleted,
    };

    set({ user: nextUser });
    saveUserCache(nextUser.userId, nextUser, get().usage);

    // 앱 전역(그룹 채팅/DM)에서 현재 유저 프로필 변경 즉시 반영
    if (fields.name !== undefined || fields.picture !== undefined) {
      try {
        const { useChatStore } = await import('./chatStore');
        useChatStore.getState().syncCurrentUserProfile({
          userId: nextUser.userId,
          name: nextUser.name,
          email: nextUser.email,
          picture: nextUser.picture,
        });
      } catch {
        // 동기화 실패는 사용자 저장 성공을 막지 않음
      }

      try {
        const { useDMConversationStore } = await import('./dmConversationStore');
        useDMConversationStore.getState().syncCurrentUserProfile({
          userId: nextUser.userId,
          name: nextUser.name,
          email: nextUser.email,
          picture: nextUser.picture,
        });
      } catch {
        // 동기화 실패는 사용자 저장 성공을 막지 않음
      }
    }
  },

  updateConsents: async (items: ConsentUpdateItem[]) => {
    const res = await apiUpdateConsents(items);
    const { user } = get();
    if (user) {
      const nextUser = { ...user, consents: res.consents };
      set({ user: nextUser });
      saveUserCache(nextUser.userId, nextUser, get().usage);
    }
    return res.consents;
  },

  updateSidebarFavoriteModes: async (modes) => {
    const { user } = get();
    if (!user) throw new Error(i18n.t('common:error.loginRequired'));

    const prevModes = user.sidebarFavoriteModes;
    const nextModes = normalizeSidebarFavoriteModes(modes);
    if (prevModes.join('|') === nextModes.join('|')) return;

    const requestSeq = ++sidebarFavoriteModesRequestSeq;
    const optimisticUser: UserInfo = {
      ...user,
      sidebarFavoriteModes: nextModes,
    };

    set({ user: optimisticUser });
    saveUserCache(optimisticUser.userId, optimisticUser, get().usage);

    try {
      const res = await updateMyProfile({ sidebar_favorite_modes: nextModes });
      if (requestSeq !== sidebarFavoriteModesRequestSeq) return;

      const currentUser = get().user;
      if (!currentUser) return;

      const committedUser: UserInfo = {
        ...currentUser,
        sidebarFavoriteModes: normalizeSidebarFavoriteModes(res.sidebar_favorite_modes),
      };
      set({ user: committedUser });
      saveUserCache(committedUser.userId, committedUser, get().usage);
    } catch (error) {
      if (requestSeq !== sidebarFavoriteModesRequestSeq) return;

      const rollbackBaseUser = get().user ?? user;
      const rollbackUser: UserInfo = {
        ...rollbackBaseUser,
        sidebarFavoriteModes: prevModes,
      };
      set({ user: rollbackUser });
      saveUserCache(rollbackUser.userId, rollbackUser, get().usage);
      throw error;
    }
  },

  updateToolsFavoriteIds: async (ids) => {
    const { user } = get();
    if (!user) throw new Error(i18n.t('common:error.loginRequired'));

    const prevIds = user.toolsFavoriteIds;
    const nextIds = normalizeToolsFavoriteIds(ids);
    if (prevIds.join('|') === nextIds.join('|')) return;

    const requestSeq = ++toolsFavoriteIdsRequestSeq;
    const optimisticUser: UserInfo = { ...user, toolsFavoriteIds: nextIds };

    set({ user: optimisticUser });
    saveUserCache(optimisticUser.userId, optimisticUser, get().usage);

    try {
      const res = await updateMyProfile({ tools_favorite_ids: nextIds });
      if (requestSeq !== toolsFavoriteIdsRequestSeq) return;

      const currentUser = get().user;
      if (!currentUser) return;

      const committedUser: UserInfo = {
        ...currentUser,
        toolsFavoriteIds: normalizeToolsFavoriteIds(res.tools_favorite_ids),
      };
      set({ user: committedUser });
      saveUserCache(committedUser.userId, committedUser, get().usage);
    } catch (error) {
      if (requestSeq !== toolsFavoriteIdsRequestSeq) return;

      const rollbackBaseUser = get().user ?? user;
      const rollbackUser: UserInfo = { ...rollbackBaseUser, toolsFavoriteIds: prevIds };
      set({ user: rollbackUser });
      saveUserCache(rollbackUser.userId, rollbackUser, get().usage);
      throw error;
    }
  },

  deleteAccount: async () => {
    await deleteMyAccount();
  },

  clear: () => {
    clearUserCache();
    set({ user: null, usage: DEFAULT_USAGE, isLoaded: false });
  },
}));
